import { EventNames, globalEventBus } from '@littlebird/event-bus';
import { stripMarkdown } from '@littlebird/markdown-utils';
import {
  ARGON_CHAT_MODE,
  DASH_CHAT_MODE,
  FAST_CHAT_MODE,
  GOD_CHAT_MODE,
  MAIN_CHAT_MODE,
  RESEARCH_CHAT_MODE,
  ULTRA_CHAT_MODE,
  type AskUserAnswers,
  isAgentThread,
} from '@littlebird/sdk';
import {
  makeAutoObservable,
  ObservableMap,
  runInAction,
  untracked,
} from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { isAgentsUxEnabled } from '@/features/agents/agents.utils';
import type { MentionItem } from '@/features/mention';
import { tabStore } from '@/features/tabs/tabs.store';
import { littlebirdSdk } from '@/lib/sdk-init';
import { AppCapability, isCapable } from '@/utils';
import { navigate } from '@/utils/navigation-service';

import { electronApis } from '../electron-api';

import {
  THREAD_DID_BECOME_INVISIBLE,
  THREAD_DID_BECOME_VISIBLE,
  localChatEventsBus,
} from './chat.events';

export enum ThreadBackgroundState {
  Background = 'background',
  BackgroundWithActivity = 'background_with_activity',
  BackgroundWithActivityNotified = 'background_with_activity_notified',
}

export type AccountPreferences = Record<string, string[]>;
export type ThreadCompactionStatus = 'triggered' | 'applied';

export type ThreadCompactionBadgeState = {
  status: ThreadCompactionStatus;
  afterUserMessageId: string | null;
  summary?: string | null;
};

export type TurnCapHitReason = 'step' | 'tool_calls';

export type ThreadTurnCapHitState = {
  reason: TurnCapHitReason;
  assistantMessageId: string;
};

export type ThreadImageGenQuotaBadgeState = {
  afterUserMessageId: string;
  messageLimit: number | null;
  nextAllowedTime: string | null;
  userErrorMessage: string | null;
};

/** Why a thread's queue waits for a press of its button instead of dispatching on its own; `send_failed` also covers a failed check of an earlier steer request */
export type QueueHoldReason = 'stop_unacknowledged' | 'send_failed';

export type QueuedChatMessage = {
  /** Also the server id in a steer request; a normal send uses a new id, so the server never reads it as a repeat */
  id: string;
  draftId: string;
  content: string;
  attachmentCount: number;
  selectedTextCount: number;
  mode: string;
  mentions: MentionItem[];
  askUserAnswers?: AskUserAnswers;
  /** App and meeting context captured when the message was queued, sent with it */
  context?: string;
  /** A steer request for it, or a later check of it, failed or got a status this client does not recognize, so only the server knows whether a turn took it in */
  steerOutcomeUnknown?: boolean;
  /** Its place among the messages steered in this session, kept once it is steered, so steered messages that return to the queue keep their order */
  steerOrder?: number;
};

type PersistedThreadCompactionBadgeState = ThreadCompactionBadgeState & {
  message?: string;
};

const toThreadCompactionBadgeState = (
  badgeState: PersistedThreadCompactionBadgeState
): ThreadCompactionBadgeState => ({
  status: badgeState.status,
  afterUserMessageId: badgeState.afterUserMessageId,
  summary: badgeState.summary ?? badgeState.message,
});

export const normalizeThreadCompactionBadgeStates = (
  badgeStates:
    | PersistedThreadCompactionBadgeState
    | PersistedThreadCompactionBadgeState[]
    | undefined
): ThreadCompactionBadgeState[] => {
  if (!badgeStates) {
    return [];
  }

  if (Array.isArray(badgeStates)) {
    return badgeStates.map(toThreadCompactionBadgeState);
  }

  return [toThreadCompactionBadgeState(badgeStates)];
};

export const defaultUserAllowedChatModes: string[] = [
  MAIN_CHAT_MODE,
  FAST_CHAT_MODE,
  RESEARCH_CHAT_MODE,
  DASH_CHAT_MODE,
  ULTRA_CHAT_MODE,
  GOD_CHAT_MODE,
  ARGON_CHAT_MODE,
];

export const NEW_CHAT_MODE_KEY = 'NEW_CHAT_MODE_KEY';
const RECENT_CHAT_MODES_LIMIT = 10;
const NOTIFICATION_EXCERPT_LENGTH = 100;

export class ChatStore {
  chatModeMap: Record<string, string> = {};
  /** Modes most recently set, newest first, for the agent picker's Recent section */
  recentChatModes: string[] = [];

  animatingMessageIds = new ObservableMap<string, string>();
  // Per-thread ownership token, bumped on every claim or external set so an
  // older instance's release doesn't clear a slot a newer instance owns.
  animatingSlotTokens = new ObservableMap<string, number>();
  private nextAnimatingSlotToken = 0;
  connectIntegrationsBannerDismissed = false;

  accountPreferences: AccountPreferences = {};

  threadsState = new ObservableMap<string, ThreadBackgroundState>();
  compactionByThread = new ObservableMap<
    string,
    ThreadCompactionBadgeState[]
  >();
  turnCapHitsByThread = new ObservableMap<string, ThreadTurnCapHitState[]>();
  imageGenQuotaBadgesByThread = new ObservableMap<
    string,
    ThreadImageGenQuotaBadgeState[]
  >();
  /** Ephemeral draft-backed messages waiting for the current turn to finish */
  queuedMessagesByThread = new ObservableMap<string, QueuedChatMessage[]>();
  /** Threads whose queue waits for a press of its button after a failed stop, send or steer check */
  queueHoldByThread = new ObservableMap<string, QueueHoldReason>();
  /** The turn an unacknowledged stop was aimed at: its late events must not lift the hold, a new turn does */
  queueHoldTurnByThread = new ObservableMap<string, string>();
  /** The queued message each thread is dispatching, so a remounted composer does not send it again */
  queuedSendInFlightByThread = new ObservableMap<string, string>();
  /** Messages the server has (`accepted` or `delivered`), shown as steering the response until the chat has them */
  steeredMessagesByThread = new ObservableMap<string, QueuedChatMessage[]>();
  /** Numbers steered messages in the order they were steered */
  private nextSteerOrder = 0;

  private stopSideEffects: (() => void) | null = null;

  start(): () => void {
    if (this.stopSideEffects) return this.stopSideEffects;
    const subscriptions = [
      // Event handlers for background activity
      globalEventBus.on(EventNames.CHAT_NEW_ASSISTANT_HIDDEN_TEXT, ({ data }) =>
        this.markThreadAsActive(data.threadId)
      ),

      globalEventBus.on(EventNames.CHAT_NEW_ASSISTANT_THOUGHT, ({ data }) =>
        this.markThreadAsActive(data.threadId)
      ),

      // CHAT_ERROR notifies for errors the server reports. CHAT_SEND_FAILED reports client-side send
      // failures, whose send site owns their error UI, and busy refusals, which the listener below reports
      globalEventBus.on(EventNames.CHAT_ERROR, ({ data }) => {
        this.markThreadAsActiveAndNotify(data.threadId, data.messageId);
      }),

      // A message the server refused because a response holds the chat; the SDK marks it failed and the response goes on
      globalEventBus.on(EventNames.CHAT_SEND_FAILED, ({ data }) => {
        if (data.reason !== 'chat_busy') return;
        toast.error("Your message wasn't sent", {
          id: `chat-busy-${data.userMessageId}`,
          description:
            'A response is still running in this chat. Send your message again when it ends.',
        });
      }),

      localChatEventsBus.on(THREAD_DID_BECOME_VISIBLE, ({ data }) =>
        this.markThreadVisible(data.threadId)
      ),

      localChatEventsBus.on(THREAD_DID_BECOME_INVISIBLE, ({ data }) =>
        this.markThreadInvisible(data.threadId)
      ),

      globalEventBus.on(
        EventNames.CHAT_ASSISTANT_DID_FINISH_MESSAGES,
        ({ data }) => {
          const messageId = data.messageIds?.[0] ?? '';
          this.markThreadAsActiveAndNotify(data.threadId, messageId);
        }
      ),

      globalEventBus.on(EventNames.CHAT_COMPACTION_STATUS, ({ data }) => {
        const { threadId, status } = data;
        const afterUserMessageId =
          data.afterUserMessageId !== undefined
            ? data.afterUserMessageId
            : findLastUserMessageId(threadId);

        littlebirdSdk.logger.info('ChatStore: updated compaction badge state', {
          threadId,
          status,
          afterUserMessageId,
        });

        runInAction(() => {
          const existingBadgeStates = normalizeThreadCompactionBadgeStates(
            this.compactionByThread.get(threadId)
          );
          this.compactionByThread.set(
            threadId,
            upsertThreadCompactionBadge(existingBadgeStates, {
              status,
              afterUserMessageId,
              summary: data.summary,
            })
          );
        });
      }),

      globalEventBus.on(EventNames.CHAT_TURN_CAP_HIT, ({ data }) => {
        const { threadId, reason, assistantMessageId } = data;
        if (!assistantMessageId) return;

        littlebirdSdk.logger.info('ChatStore: recorded turn cap-hit', {
          threadId,
          reason,
          assistantMessageId,
        });

        runInAction(() => {
          const existing = this.turnCapHitsByThread.get(threadId) ?? [];
          if (
            existing.some((s) => s.assistantMessageId === assistantMessageId)
          ) {
            return;
          }
          this.turnCapHitsByThread.set(threadId, [
            ...existing,
            { reason, assistantMessageId },
          ]);
        });
      }),

      globalEventBus.on(
        EventNames.CHAT_IMAGE_GENERATION_QUOTA_EXCEEDED,
        ({ data }) => {
          const {
            threadId,
            afterUserMessageId,
            messageLimit,
            nextAllowedTime,
            userErrorMessage,
          } = data;
          if (!afterUserMessageId) return;

          littlebirdSdk.logger.info(
            'ChatStore: recorded image-generation quota badge',
            {
              threadId,
              afterUserMessageId,
              messageLimit,
              nextAllowedTime,
            }
          );

          runInAction(() => {
            const existing =
              this.imageGenQuotaBadgesByThread.get(threadId) ?? [];
            if (
              existing.some((s) => s.afterUserMessageId === afterUserMessageId)
            ) {
              return;
            }
            this.imageGenQuotaBadgesByThread.set(threadId, [
              ...existing,
              {
                afterUserMessageId,
                messageLimit,
                nextAllowedTime,
                userErrorMessage,
              },
            ]);
          });
        }
      ),
    ];
    const stop = () => {
      for (const subscription of subscriptions) subscription.unsubscribe();
      if (this.stopSideEffects === stop) this.stopSideEffects = null;
    };
    this.stopSideEffects = stop;
    return stop;
  }

  constructor() {
    makeAutoObservable<this, 'stopSideEffects'>(this, {
      stopSideEffects: false,
    });
    makePersistable(this, {
      name: 'chatStore',
      properties: [
        'chatModeMap',
        'recentChatModes',
        'connectIntegrationsBannerDismissed',
        'accountPreferences',
        'compactionByThread',
        'turnCapHitsByThread',
        'imageGenQuotaBadgesByThread',
      ],
      storage: window.localStorage,
    }).then(() => {
      this.reconcileHydratedCompactionBadges();
    });
  }

  private reconcileHydratedCompactionBadges(): void {
    for (const [threadId, badgeStates] of this.compactionByThread.entries()) {
      const appliedBadgeStates = normalizeThreadCompactionBadgeStates(
        badgeStates as ThreadCompactionBadgeState | ThreadCompactionBadgeState[]
      ).filter((badgeState) => badgeState.status === 'applied');

      if (appliedBadgeStates.length === 0) {
        this.compactionByThread.delete(threadId);
        continue;
      }

      this.compactionByThread.set(threadId, appliedBadgeStates);
    }
  }

  setChatMode(
    mode: string,
    chatId?: string,
    newChatId?: string,
    allowedChatModes?: string[]
  ) {
    if (
      !allowedChatModes?.includes(mode) &&
      !defaultUserAllowedChatModes.includes(mode)
    )
      return;

    this.recentChatModes = [
      mode,
      ...this.recentChatModes.filter((recentMode) => recentMode !== mode),
    ].slice(0, RECENT_CHAT_MODES_LIMIT);

    const threadId = chatId ?? newChatId;
    if (threadId) {
      littlebirdSdk.stores.threads
        .setChatMode(threadId, mode)
        .catch((error) => {
          littlebirdSdk.logger.error('ChatStore: chat mode not saved', {
            metadata: { error, threadId, mode },
          });
        });
    }
    if (!chatId) {
      this.chatModeMap = { ...this.chatModeMap, [NEW_CHAT_MODE_KEY]: mode };
    }
  }

  getAnimatingMessageId(threadId: string) {
    return this.animatingMessageIds.get(threadId);
  }
  setAnimatingMessageId(threadId: string, messageId: string) {
    // Bump the token so any in-flight useAnimationSlot.release() with a
    // stale token becomes a no-op.
    this.nextAnimatingSlotToken += 1;
    this.animatingSlotTokens.set(threadId, this.nextAnimatingSlotToken);
    this.animatingMessageIds.set(threadId, messageId);
  }
  claimAnimatingSlot(threadId: string, messageId: string): number {
    this.nextAnimatingSlotToken += 1;
    const token = this.nextAnimatingSlotToken;
    this.animatingSlotTokens.set(threadId, token);
    this.animatingMessageIds.set(threadId, messageId);
    return token;
  }
  releaseAnimatingSlotIfOwner(threadId: string, ownToken: number) {
    if (this.animatingSlotTokens.get(threadId) !== ownToken) return;
    this.animatingSlotTokens.delete(threadId);
    this.animatingMessageIds.delete(threadId);
  }
  clearAnimatingSlot(threadId: string) {
    // Bump the token first so any concurrent useAnimationSlot.release()
    // holding the prior token sees an ownership mismatch and no-ops.
    this.nextAnimatingSlotToken += 1;
    this.animatingSlotTokens.delete(threadId);
    this.animatingMessageIds.delete(threadId);
  }

  enqueueMessage(threadId: string, message: QueuedChatMessage) {
    const queuedMessages = this.queuedMessagesByThread.get(threadId) ?? [];
    this.queuedMessagesByThread.set(threadId, [...queuedMessages, message]);
  }

  setSteerOutcomeUnknown(
    threadId: string,
    messageId: string,
    steerOutcomeUnknown: boolean
  ) {
    const queuedMessages = this.queuedMessagesByThread.get(threadId);
    if (!queuedMessages) return;
    this.queuedMessagesByThread.set(
      threadId,
      queuedMessages.map((message) =>
        message.id === messageId ? { ...message, steerOutcomeUnknown } : message
      )
    );
  }

  /** Moves a message the server has from the queue to the list shown until the chat has it */
  markQueuedMessageSteered(
    threadId: string,
    messageId: string
  ): QueuedChatMessage | undefined {
    const queuedMessage = this.removeQueuedMessage(threadId, messageId);
    if (!queuedMessage) return undefined;
    const steeredMessage = {
      ...queuedMessage,
      steerOrder: queuedMessage.steerOrder ?? this.nextSteerOrder++,
    };
    const steeredMessages = this.steeredMessagesByThread.get(threadId) ?? [];
    this.steeredMessagesByThread.set(threadId, [
      ...steeredMessages,
      steeredMessage,
    ]);
    return steeredMessage;
  }

  removeSteeredMessage(
    threadId: string,
    messageId: string
  ): QueuedChatMessage | undefined {
    const steeredMessages = this.steeredMessagesByThread.get(threadId) ?? [];
    const removedMessage = steeredMessages.find(
      (message) => message.id === messageId
    );
    const remainingMessages = steeredMessages.filter(
      (message) => message.id !== messageId
    );
    if (remainingMessages.length === 0) {
      this.steeredMessagesByThread.delete(threadId);
    } else {
      this.steeredMessagesByThread.set(threadId, remainingMessages);
    }
    return removedMessage;
  }

  /**
   * Moves steered messages to the front of the queue when the server no longer has them or they
   * could not be checked. Steered messages left the front of the queue, so they go back ahead of
   * the messages never steered, in the order they were steered, including those that returned at
   * an earlier check. `steerOutcomeUnknown` marks one that could not be checked, so the next send
   * checks it first
   */
  returnSteeredMessagesToQueue(
    threadId: string,
    messagesToReturn: { id: string; steerOutcomeUnknown: boolean }[]
  ) {
    const returnedMessages = messagesToReturn.flatMap(
      ({ id, steerOutcomeUnknown }) => {
        const message = this.removeSteeredMessage(threadId, id);
        return message ? [{ ...message, steerOutcomeUnknown }] : [];
      }
    );
    if (returnedMessages.length === 0) return;
    const queuedMessages = this.queuedMessagesByThread.get(threadId) ?? [];
    const neverSteered = queuedMessages.filter(
      ({ steerOrder }) => steerOrder === undefined
    );
    const steeredBefore = [
      ...queuedMessages.filter(({ steerOrder }) => steerOrder !== undefined),
      ...returnedMessages,
    ].sort((a, b) => (a.steerOrder ?? 0) - (b.steerOrder ?? 0));
    this.queuedMessagesByThread.set(threadId, [
      ...steeredBefore,
      ...neverSteered,
    ]);
  }

  removeQueuedMessage(
    threadId: string,
    messageId: string
  ): QueuedChatMessage | undefined {
    const queuedMessages = this.queuedMessagesByThread.get(threadId);
    if (!queuedMessages) return undefined;
    const removedMessage = queuedMessages.find(
      (message) => message.id === messageId
    );
    if (!removedMessage) return undefined;

    const remainingMessages = queuedMessages.filter(
      (message) => message.id !== messageId
    );
    if (remainingMessages.length === 0) {
      this.queuedMessagesByThread.delete(threadId);
      this.releaseQueue(threadId);
    } else {
      this.queuedMessagesByThread.set(threadId, remainingMessages);
    }
    return removedMessage;
  }

  /**
   * Whether anything is queued, held, dispatching, or steered and not yet in the chat, so a new
   * send must join the queue to keep order
   */
  hasQueueState(threadId: string): boolean {
    return (
      this.queuedMessagesByThread.has(threadId) ||
      this.steeredMessagesByThread.has(threadId) ||
      this.queueHoldByThread.has(threadId) ||
      this.queuedSendInFlightByThread.has(threadId)
    );
  }

  holdQueue(threadId: string, reason: QueueHoldReason, turnMessageId?: string) {
    this.queueHoldByThread.set(threadId, reason);
    if (turnMessageId === undefined) {
      this.queueHoldTurnByThread.delete(threadId);
    } else {
      this.queueHoldTurnByThread.set(threadId, turnMessageId);
    }
  }

  releaseQueue(threadId: string) {
    this.queueHoldByThread.delete(threadId);
    this.queueHoldTurnByThread.delete(threadId);
  }

  /** Claims the thread's single dispatch slot; false when another dispatch is still running */
  beginQueuedSend(threadId: string, messageId: string): boolean {
    if (this.queuedSendInFlightByThread.has(threadId)) return false;
    this.queuedSendInFlightByThread.set(threadId, messageId);
    return true;
  }

  endQueuedSend(threadId: string, messageId: string) {
    if (this.queuedSendInFlightByThread.get(threadId) === messageId) {
      this.queuedSendInFlightByThread.delete(threadId);
    }
  }

  setConnectIntegrationsBannerDismissed(v: boolean) {
    this.connectIntegrationsBannerDismissed = v;
  }

  setAccountPreferences(
    updater: (prev: AccountPreferences) => AccountPreferences
  ) {
    this.accountPreferences = updater(this.accountPreferences);
  }

  markThreadUnread(threadId: string) {
    this.threadsState.set(
      threadId,
      ThreadBackgroundState.BackgroundWithActivityNotified
    );
  }

  markThreadVisible(threadId: string) {
    this.threadsState.delete(threadId);
  }

  markThreadInvisible(threadId: string) {
    this.threadsState.set(threadId, ThreadBackgroundState.Background);
  }

  markThreadAsActive(threadId: string) {
    if (
      this.threadsState.has(threadId) &&
      this.threadsState.get(threadId) === ThreadBackgroundState.Background
    ) {
      this.threadsState.set(
        threadId,
        ThreadBackgroundState.BackgroundWithActivity
      );
    }
  }

  markThreadAsActiveAndNotify(threadId: string, messageId: string) {
    if (
      this.threadsState.has(threadId) &&
      this.threadsState.get(threadId) !==
        ThreadBackgroundState.BackgroundWithActivityNotified
    ) {
      this.threadsState.set(
        threadId,
        ThreadBackgroundState.BackgroundWithActivityNotified
      );
      notifyThreadActivity(threadId, messageId);
    }
  }

  getThreadBackgroundStatus(threadId: string) {
    return this.threadsState.get(threadId);
  }

  async getLastUserMessage(threadId: string): Promise<string | undefined> {
    const message =
      await littlebirdSdk.storage.entities.messages?.getLastUserMessageForThread(
        threadId
      );
    return message?.content;
  }
}

export const chatStore = new ChatStore();
chatStore.start();

// Token-based ownership: the chatStore returns a unique token on claim and
// only honours a release that presents the same token. Same-message remounts
// (virtualization, route churn) get a fresh token, so the old instance's
// release becomes a no-op once the new instance has claimed.
export function useAnimationSlot(threadId: string, messageId: string) {
  const tokenRef = useRef<number | null>(null);

  const claim = useCallback(() => {
    tokenRef.current = chatStore.claimAnimatingSlot(threadId, messageId);
  }, [threadId, messageId]);

  const release = useCallback(() => {
    const token = tokenRef.current;
    if (token === null) return;
    tokenRef.current = null;
    untracked(() => chatStore.releaseAnimatingSlotIfOwner(threadId, token));
  }, [threadId]);

  useEffect(() => release, [release]);

  return { claim, release };
}

export function useLastUserMessage(
  threadId: string,
  refreshKey?: string
): string | undefined {
  const [lastUserMessage, setLastUserMessage] = useState<string | undefined>();

  useEffect(() => {
    void chatStore.getLastUserMessage(threadId).then(setLastUserMessage);
  }, [threadId, refreshKey]);

  return lastUserMessage;
}

const upsertThreadCompactionBadge = (
  badgeStates: ThreadCompactionBadgeState[],
  nextBadgeState: ThreadCompactionBadgeState
): ThreadCompactionBadgeState[] => {
  const existingBadgeIndex = badgeStates.findIndex(
    (badgeState) =>
      badgeState.afterUserMessageId === nextBadgeState.afterUserMessageId
  );

  if (existingBadgeIndex === -1) {
    return [...badgeStates, nextBadgeState];
  }

  const existingBadgeState = badgeStates[existingBadgeIndex];
  const shouldPreserveAppliedState =
    existingBadgeState.status === 'applied' &&
    nextBadgeState.status === 'triggered';

  if (shouldPreserveAppliedState) {
    return badgeStates;
  }

  return badgeStates.map((badgeState, index) =>
    index === existingBadgeIndex ? nextBadgeState : badgeState
  );
};

const findLastUserMessageId = (threadId: string): string | null => {
  const messages =
    littlebirdSdk.stores.messages.getMessagesByThreadId(threadId);

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role === 'user') {
      return message.id;
    }
  }

  return null;
};

const openCompletedThread = (threadId: string) => {
  const chatPath = `/chats/${threadId}`;
  const existingTab = tabStore.showTabBar
    ? tabStore.tabs.find((tab) => tab.route.path === chatPath)
    : undefined;

  if (existingTab) {
    tabStore.activateTab(existingTab.id);
    navigate({
      to: existingTab.route.path,
      search: existingTab.route.search as Record<string, string>,
    });
    return;
  }

  navigate({
    to: '/chats/$chatId',
    params: {
      chatId: threadId,
    },
  });
};

const notifyThreadActivity = async (threadId: string, messageId: string) => {
  const thread = littlebirdSdk.stores.threads.get(threadId);
  const message = littlebirdSdk.stores.messages.get(messageId);

  const title = `Complete: ${thread?.title ?? 'Thread Activity'}`;
  const content = message?.content ?? 'New activity in your thread';
  const description = stripMarkdown(
    content.length > NOTIFICATION_EXCERPT_LENGTH
      ? content.substring(0, NOTIFICATION_EXCERPT_LENGTH) + '...'
      : content
  );

  // Also keeps this notification behind Hummingbird's for a reply sent from
  // Hummingbird: both use the same id and the main process shows the first
  await new Promise((resolve) => setTimeout(resolve, 1000));

  if (chatStore.getThreadBackgroundStatus(threadId) === undefined) return;

  // Use document.hasFocus() instead of document.hidden because
  // document.hidden only becomes true on Cmd+H (minimize) on macOS,
  // NOT on Cmd+Tab or clicking another app. hasFocus() correctly
  // tracks Electron window blur/focus state.
  if (document.hasFocus()) {
    if (thread && isAgentsUxEnabled() && isAgentThread(thread)) return;
    toast.info(title, {
      id: `thread-activity-${threadId}-${messageId}`,
      description,
      closeButton: true,
      action: {
        label: 'Open',
        onClick() {
          openCompletedThread(threadId);
        },
      },
    });

    return;
  }

  if (isCapable(AppCapability.WebExclusive)) return;

  electronApis.lbNotifications.native.sendNotification({
    id: `thread-activity-${threadId}-${messageId}`,
    title,
    message: description,
    sound: 'done',
    transient: true,
    route: {
      to: '/chats/$chatId',
      params: { chatId: threadId },
    },
  });
};
